import { randomUUID, randomFloat, randomInteger, randomWords, randomBoolean } from 'utils/random';
import titleCase from 'utils/title-case';
import { Summary, Feeds, FishGroup } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';

const feedSources = ['Manual', 'BestMix', 'Imported'];

/**
 * Generates a random FishGroup object.
 *
 * @returns {FishGroup} A randomly generated FishGroup object.
 */
function getRandomFishGroup(): FishGroup {
  const fishGroupNames = [
    'Atlantic Salmon G1',
    'Atlantic Salmon G2',
    'AS Smolt 2024-1',
    'AS Smolt 2024-2',
    'AS Smolt 2025-1',
    'Trout Gen A',
    'Trout Gen B',
  ];

  return {
    id: randomUUID(),
    name: fishGroupNames[randomInteger(fishGroupNames.length - 1)],
  };
}

/**
 * Generates an array of random Feeds objects.
 *
 * @param count The number of feeds to generate. Defaults to 3.
 * @returns {Feeds[]} A randomly generated Feeds object array.
 */
function getRandomFeeds(count = 3): Feeds[] {
  return Array.from({ length: count }, (_, i) => ({
    fishSizeStart: Math.round((randomFloat(4000, 0) + 500) / 100) * 100,
    feedId: randomUUID(),
    price: Math.round(randomFloat(200, 2) / 10) * 10,
    feedSource: feedSources[randomInteger(feedSources.length - 1)],
    feedName: titleCase(randomWords(3)),
    isActive: randomBoolean(),
    scenarioActive: randomBoolean(),
  })).sort((a, b) => a.fishSizeStart - b.fishSizeStart);
}

/**
 * Generates a random Summary object.
 *
 * @returns {Summary} A randomly generated Summary object.
 */
function getRandomSummary(): Summary {
  const feedPlanNames = [
    'Plan 1',
    'Feed Program Standard',
    'Growth Optimized Plan',
    'Low Cost Plan',
    'High Performance Feed',
    'Sustainable Mix',
    'Premium Growth',
  ];

  return {
    id: randomUUID(),
    feedPlanId: randomUUID(),
    feedPlan: feedPlanNames[randomInteger(feedPlanNames.length - 1)],
    feeds: getRandomFeeds(randomInteger(4) + 1),
    feedPlanMaxWeeks: randomInteger(52) + 10,
    feedPlanMaxWeight: randomFloat(8000, 0) + 2000,
    finalWeightGrams: randomFloat(6000, 1) + 1000,
    daysToReach: randomInteger(300) + 60,
    weightGainGrams: randomFloat(5000, 1) + 500,
    feedIntakeGrams: randomFloat(8000, 1) + 1000,
    bFcr: randomFloat(2, 2) + 0.8,
    eFcr: randomFloat(2.5, 2) + 1,
    feedCostOfHogGain: randomFloat(100, 2) + 20,
    potentialSalesValue: randomFloat(100, 2) + 20,
    totalCost: randomFloat(1000, 2) + 500,
    appliedAs: randomBoolean() ? 'Initial' : 'Override',
    carcassYieldPercent: randomFloat(20, 1) + 70,
    filletFatPercent: randomFloat(15, 1) + 5,
    hogGainGrams: randomFloat(4000, 1) + 300,
    globalWarmingPotentialCo2KgPerKgGrowth: randomFloat(3, 2) + 1,
    globalWarmingPotentialCo2KgPerKgFeed: randomFloat(2, 2) + 0.5,
    averageFeedCostPerKgFeed: randomFloat(2000, 0) + 1000,
    feedCostOfGrowth: randomFloat(50, 2) + 10,
    costBreakdown: {
      feedCostTotal: randomFloat(1000, 2) + 500,
      smoltCostTotal: randomFloat(500, 2) + 200,
      operationsCostTotal: randomFloat(800, 2) + 300,
      treatmentCostTotal: randomFloat(400, 2) + 100,
      harvestCostTotal: randomFloat(600, 2) + 200,
    },
  };
}

/**
 * Generates an array of random Summary objects.
 *
 * @param count The number of summaries to generate. Defaults to 5.
 * @returns {Summary[]} A randomly generated Summary object array.
 */
export function getRandomSummaries(count = 5): Summary[] {
  return Array.from({ length: count }, () => getRandomSummary());
}

export const scenariosResultMock: Summary[] = getRandomSummaries(5);
